import { CommonModule } from "@angular/common"
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core"
import { FormControl, ReactiveFormsModule } from "@angular/forms"
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from "@angular/material/autocomplete"
import { MatFormFieldModule } from "@angular/material/form-field"
import { MatIconModule } from "@angular/material/icon"
import { MatInputModule } from "@angular/material/input"
import { Subscription } from "rxjs"
import { Station } from "../../models/station.model"

@Component({
  selector: "app-station-select-field",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatIconModule,
  ],
  templateUrl: "./station-select-field.component.html",
  styleUrl: "./station-select-field.component.css",
})
export class StationSelectFieldComponent implements OnInit, OnChanges, OnDestroy {
  @Input() stations: Station[] = []
  @Input() label = "Station"
  @Input() placeholder = "Search and select station..."
  @Input() hint = ""
  @Input() required = false
  @Input() stationId: number | null = null
  @Output() stationIdChange = new EventEmitter<number | null>()

  searchControl = new FormControl<string>("", { nonNullable: true })
  visibleStations: Station[] = []
  private sub?: Subscription

  ngOnInit(): void {
    this.sub = this.searchControl.valueChanges.subscribe((value) => {
      this.updateVisibleStations((value || "").trim())
      this.syncSelectionFromInput(value || "")
    })
    this.syncInputFromStationId()
    this.updateVisibleStations(this.searchControl.value.trim())
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["stations"] || changes["stationId"]) {
      this.syncInputFromStationId()
      this.updateVisibleStations(this.searchControl.value.trim())
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe()
  }

  stationLabel(station: Station): string {
    return `${station.call_sign} - ${station.name}`
  }

  onPanelOpened(): void {
    this.updateVisibleStations(this.searchControl.value.trim())
  }

  onStationSelected(event: MatAutocompleteSelectedEvent): void {
    const id = Number(event.option.value)
    const station = this.stations.find((s) => s.id === id)
    if (!station) return

    this.stationId = station.id
    this.stationIdChange.emit(station.id)
    this.searchControl.setValue(this.stationLabel(station), { emitEvent: false })
    this.updateVisibleStations("")
  }

  onBlur(): void {
    const selected = this.stations.find((s) => s.id === this.stationId)
    if (selected && this.searchControl.value !== this.stationLabel(selected)) {
      this.searchControl.setValue(this.stationLabel(selected), { emitEvent: false })
    } else if (!selected && this.searchControl.value.trim()) {
      this.searchControl.setValue("", { emitEvent: false })
      this.stationId = null
      this.stationIdChange.emit(null)
    }
    this.updateVisibleStations("")
  }

  private syncInputFromStationId(): void {
    const selected = this.stations.find((s) => s.id === this.stationId)
    const next = selected ? this.stationLabel(selected) : ""
    if (this.searchControl.value !== next) {
      this.searchControl.setValue(next, { emitEvent: false })
    }
  }

  private syncSelectionFromInput(value: string): void {
    const selected = this.stations.find((s) => s.id === this.stationId)
    if (selected && value === this.stationLabel(selected)) {
      return
    }
    if (selected && value !== this.stationLabel(selected)) {
      this.stationId = null
      this.stationIdChange.emit(null)
    }
  }

  private updateVisibleStations(query: string): void {
    const q = query.toLowerCase()
    this.visibleStations = !q
      ? [...this.stations]
      : this.stations.filter((station) => {
          const name = (station.name || "").toLowerCase()
          const callSign = (station.call_sign || "").toLowerCase()
          return name.includes(q) || callSign.includes(q)
        })
  }
}
